import React, { useState, useMemo } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import { List } from "react-window";
import { useQuery } from "@apollo/client/react";

import AssetsWrap from "../../../AssetsWrap";
import SortAssets from "../../../SortAssets";
import AssetListItem from "../ItemLookup/AssetListItem";
import { ONSITE_INVENTORY_COLLECTION_QUERY } from "src/constants/graphql/queries";
import { useProject } from "src/contexts/project";

const ITEM_HEIGHT = 58;
const LIST_HEIGHT = 300;

const OnsiteItemRow = ({ style, index, items, selectedItems, onToggleItem, project, closeParam, displayItemId, order, showCheckbox }) => {
  const asset = items[index];
  return (
    <AssetListItem
      displayItemId={displayItemId}
      order={order}
      asset={asset}
      style={style}
      project={project}
      closeParam={closeParam}
      selected={selectedItems?.has(asset?.id)}
      onToggle={onToggleItem}
      showCheckbox={showCheckbox}
    />
  );
};

export default function OnsiteInventorySearch({
  selectedItems,
  onToggleItem,
  onSelectAll,
}) {
  const project = useProject();
  const [search, setSearch] = useState("");
  const [order, setOrder] = useState("item_id.desc");

  const { data, loading } = useQuery(ONSITE_INVENTORY_COLLECTION_QUERY, {
    variables: {
      projectId: project?.id,
      search: search || undefined,
      order: { [order.split(".")[0]]: order.split(".")[1] },
      type: "Asset",
      hasImportedAssets: false,
      hideDepletedAssets: false,
    },
    skip: !project?.id,
  });

  const collection = data?.assetsCollection?.collection || [];
  const totalCount = data?.assetsCollection?.metadata?.totalCount || 0;

  const allSelected = useMemo(
    () => collection.length > 0 && collection.every(item => selectedItems?.has(item.id)),
    [collection, selectedItems]
  );

  const someSelected = useMemo(
    () => collection.some(item => selectedItems?.has(item.id)),
    [collection, selectedItems]
  );

  const indeterminate = someSelected && !allSelected;

  const handleSelectAllClick = event => {
    if (onSelectAll) {
      onSelectAll(
        collection.map(item => item.id),
        event.target.checked
      );
    }
  };

  const bulkProps = useMemo(
    () => ({
      label: onSelectAll ? (
        <Checkbox
          checked={allSelected}
          indeterminate={indeterminate}
          onChange={handleSelectAllClick}
          color="primary"
          size="small"
          sx={{ ml: 0.25, mr: 1 }}
          disableRipple
        />
      ) : null,
    }),
    [allSelected, indeterminate, onSelectAll, collection]
  );

  const rowProps = useMemo(
    () => ({
      items: collection,
      selectedItems,
      onToggleItem,
      project,
      closeParam: "onsiteInventory",
      displayItemId: true,
      order,
      showCheckbox: true,
    }),
    [collection, selectedItems, onToggleItem, project, order]
  );

  return (
    <AssetsWrap height={undefined} loading={loading} search={search} setSearch={setSearch}>
      <SortAssets bulkProps={bulkProps} order={order} setOrder={setOrder} />
      <Box display="flex" flexDirection="column" style={{ height: LIST_HEIGHT, overflowY: "auto" }}>
        {collection.length === 0 && !loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={LIST_HEIGHT}>
            <Box color="text.secondary">
              {search ? `No items found matching "${search}"` : "No items available"}
            </Box>
          </Box>
        ) : (
          <List
            style={{ height: LIST_HEIGHT, width: "100%" }}
            rowCount={collection.length}
            rowHeight={ITEM_HEIGHT}
            rowProps={rowProps}
            rowComponent={OnsiteItemRow}
          />
        )}
      </Box>
    </AssetsWrap>
  );
}
